import { useEffect, useCallback } from 'react';
import { Search } from '@bfe/moly';
import clsx from 'clsx';
import { CSSProperties, useRef, useState } from 'react';
import { getDefaultIndex } from '../../../store/config';
import { useLocale } from '../../../store/i18nMessage';
import { SearchHistory } from '../SearchHistory';
import { SearchNetwork } from '../SearchNetwork';
import { SearchContent } from '../SearchContent';
import { searchStorageKey } from '../../../utils/constants';
import { getSupportedChains, SwapChain, SwapScene } from '../api';

import styles from './index.module.scss';

export type SearchDialogProps = {
  className?: string;
  style?: CSSProperties;
  visible: boolean;
  onClose?: () => void;
  left?: number | string;
};

// 判断是否为空值
export const isNullOrUndefined = (v: unknown) => {
  try {
    return Object.prototype.toString.call(v) === '[object Undefined]' || Object.prototype.toString.call(v) === '[object Null]';
  } catch {
    return false;
  }
};

export const SearchDialog = ({ visible, className, style, onClose, left = '50vw' }: SearchDialogProps) => {
  const { t } = useLocale();
  const [searchText, setSearchText] = useState('');
  const [debonceSearchText, setDebonceSearchText] = useState('');
  const [activeChainId, setActiveChainId] = useState('');
  // eslint-disable-next-line
  const timer = useRef<NodeJS.Timeout>();
  const modalRef = useRef<HTMLDivElement>(null);
  const [chains, setChains] = useState<SwapChain[]>([]);
  const [historyList, setHistoryList] = useState<string[]>(JSON.parse(localStorage.getItem(searchStorageKey) ?? '[]'));

  const searchInputRef = useRef(null);
  const overflowRef = useRef(null);

  const handleInput = (userInput: string) => {
    const search = userInput.replace(/^\s+(.*)/, '$1').replace(/(.+)\s{2,}$/, '$1 ');
    setSearchText(search);

    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setDebonceSearchText(search);
    }, 500);
  };

  const fetchChainList = async () => {
    const { result, success } = await getSupportedChains();

    if (!success) {
      // toastFail(fm('systemBusyTryLater'));
      // setFetchSwapChainsLoading(false);
      return;
    }

    // 根据scene过滤出dexpro支持的链
    const dexProChains = result?.filter((chain) => chain?.supportSceneConfig?.some((it) => it.scene === SwapScene.DexPro)) || [];
    // 目前是前端根据后端返回order进行排序
    const ascendingResult = dexProChains.sort((a, b) => {
      // 两个元素都有有效的 order
      if (!isNullOrUndefined(a?.order) && !isNullOrUndefined(b?.order)) {
        return a.order - b.order || 0;
      }
      // a 的 order 为 null，b 的 order 有有效值
      if (isNullOrUndefined(a?.order) && !isNullOrUndefined(b?.order)) {
        return 1; // 将 a 排在 b 后面
      }
      // b 的 order 为 null，a 的 order 有有效值
      if (!isNullOrUndefined(a?.order) && isNullOrUndefined(b?.order)) {
        return -1; // 将 a 排在 b 前面
      }
      // 两个元素的 order 均为 null
      return 0; // 保持原有顺序
    });
    setChains(ascendingResult);
  };

  const handleWheelOutside = useCallback((event) => {
    if (modalRef.current && !modalRef.current.contains(event.target as any)) {
      document.documentElement.style.overflow = overflowRef.current || 'visible';
      return;
    }

    if (visible) {
      document.documentElement.style.overflow = 'hidden';
    }
  }, []);

  const handleClickOutside = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      document.removeEventListener('wheel', handleWheelOutside);
      document.documentElement.style.overflow = overflowRef.current || 'visible';
      onClose?.();
    }
  };

  useEffect(() => {
    if (visible) {
      if (overflowRef.current === null) {
        overflowRef.current = document.documentElement.style.overflow as any;
        document.documentElement.style.overflow = 'hidden';
      }
      // 当弹窗打开时，添加点击事件监听器
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('wheel', handleWheelOutside);
    } else {
      // 弹窗关闭时，移除监听器
      document.removeEventListener('mousedown', handleClickOutside);
    }

    // 组件卸载时，移除监听器
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [visible]);

  useEffect(() => {
    fetchChainList();
  }, []);

  useEffect(() => {
    if (!searchText) {
      setActiveChainId('');
    }
  }, [searchText]);

  return (
    <div
      className={clsx(styles.container, 'moly-theme-component-dark', className)}
      style={{ zIndex: getDefaultIndex(), ...style, display: visible ? 'flex' : 'none', left }}
      onScroll={(e) => {
        e.preventDefault();
      }}
      ref={modalRef}
    >
      <div className={styles.header}>
        <Search
          className={clsx(styles.search, 'flex-grow bg-bds-gray-ele-line rounded-[38px] font-white')}
          ref={searchInputRef}
          value={searchText}
          onChange={(evt) => {
            handleInput(evt.target.value);
          }}
          placeholder={t('search_placeholder')}
        />
      </div>
      {visible && (
        <>
          {!!historyList.length && !debonceSearchText && (
            <SearchHistory
              historyList={historyList}
              handleInput={handleInput}
              onClear={() => {
                setHistoryList([]);
                localStorage.removeItem(searchStorageKey);
              }}
            />
          )}
          {!!debonceSearchText && (
            <SearchNetwork
              chainId={activeChainId}
              chains={chains}
              onChangeChainId={(chainId) => {
                setActiveChainId(chainId);
              }}
            />
          )}
          {
            <SearchContent
              searchText={debonceSearchText}
              chainId={activeChainId}
              onChangeHistory={(history: string[]) => {
                setHistoryList(history);
              }}
            />
          }
        </>
      )}
    </div>
  );
};
